<template>
  <div class="password-history-page">
    <div class="card history-hero">
      <div class="card-body">
        <div class="hero-content">
          <div>
            <span class="eyebrow">Employee Security</span>
            <h4>Password Change History</h4>
            <p>
              Daftar employee yang sudah melakukan perubahan password akun.
            </p>
          </div>

          <div class="history-total" aria-label="Total employee">
            <i class="ti ti-shield"></i>
            <div>
              <strong>{{ pagination.total | formatPrice }}</strong>
              <span>Employee</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <div class="history-toolbar">
          <div>
            <h5 class="mb-1">Password Updated</h5>
            <small class="text-muted">
              Informasi password dan hash tidak ditampilkan pada halaman ini.
            </small>
          </div>

          <form class="history-search" @submit.prevent="searchHistory">
            <label class="sr-only" for="password-history-search">
              Cari nomor handphone employee
            </label>
            <div class="input-group">
              <input
                id="password-history-search"
                v-model.trim="search"
                type="search"
                class="form-control"
                placeholder="Cari nomor handphone..."
                autocomplete="off"
              />
              <div class="input-group-append">
                <button class="btn btn-primary" type="submit" :disabled="loading">
                  <i class="ti ti-search"></i>
                  Cari
                </button>
              </div>
            </div>

            <button
              type="button"
              class="btn btn-light refresh-button"
              :disabled="loading"
              aria-label="Refresh password history"
              title="Refresh data"
              @click="refreshHistory"
            >
              <i class="ti ti-reload" :class="{ rotating: loading }"></i>
            </button>
          </form>
        </div>

        <div class="history-filters">
          <div class="filter-group">
            <label for="password-history-source">Sumber Perubahan</label>
            <select
              id="password-history-source"
              v-model="source"
              class="form-control"
              :disabled="loading"
              @change="applyFilters"
            >
              <option value="">Semua Sumber</option>
              <option value="forgot_password">Forgot Password</option>
              <option value="update_pin">Update PIN</option>
            </select>
          </div>

          <button
            v-if="hasActiveFilters"
            type="button"
            class="btn btn-light reset-filter"
            :disabled="loading"
            @click="resetFilters"
          >
            <i class="ti ti-close"></i>
            Reset Filter
          </button>
        </div>

        <div v-if="errorMessage" class="alert alert-danger history-message" role="alert">
          <div>
            <strong>Data belum dapat ditampilkan.</strong>
            <span>{{ errorMessage }}</span>
          </div>
          <button class="btn btn-sm btn-outline-danger" type="button" @click="fetchHistory(1)">
            Coba Lagi
          </button>
        </div>

        <div v-if="loading" class="history-loading" aria-live="polite">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Memuat data...</span>
          </div>
          <span>Memuat password history...</span>
        </div>

        <template v-else-if="!errorMessage">
          <div v-if="rows.length" class="history-table-wrapper">
            <table class="table history-table">
              <thead>
                <tr>
                  <th class="number-column">No.</th>
                  <th>Employee</th>
                  <th>NIK</th>
                  <th>Contact</th>
                  <th>Source</th>
                  <th>IP Address</th>
                  <th>Changed At</th>
                  <th class="text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in rows" :key="rowKey(row, index)">
                  <td class="number-column">
                    {{ rowNumber(index) }}
                  </td>
                  <td>
                    <div class="employee-cell">
                      <span class="employee-avatar">{{ employeeInitial(row) }}</span>
                      <div>
                        <strong>{{ employeeName(row) }}</strong>
                        <small>{{ employeePosition(row) }}</small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span class="nik-value">{{ field(row, "nik") || "-" }}</span>
                  </td>
                  <td>
                    <div class="contact-cell">
                      <span>{{ field(row, "email") || "-" }}</span>
                      <small>{{ field(row, "phone_number") || "" }}</small>
                    </div>
                  </td>
                  <td>
                    <span class="source-badge">{{ sourceLabel(row.source) }}</span>
                  </td>
                  <td>
                    <span class="nik-value">{{ row.from_ip || "-" }}</span>
                  </td>
                  <td>
                    <div class="date-cell">
                      <i class="ti ti-time"></i>
                      <span>{{ changedAt(row) }}</span>
                    </div>
                  </td>
                  <td class="text-center">
                    <span class="status-badge">
                      <i class="ti ti-check"></i>
                      Password Updated
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else class="history-empty">
            <span class="empty-icon"><i class="ti ti-lock"></i></span>
            <h5>{{ hasActiveFilters ? "Employee tidak ditemukan" : "Belum ada password history" }}</h5>
            <p>
              {{
                hasActiveFilters
                  ? "Coba gunakan nomor handphone atau sumber perubahan yang berbeda."
                  : "Employee yang sudah mengganti password akan tampil di sini."
              }}
            </p>
            <button v-if="hasActiveFilters" type="button" class="btn btn-light" @click="resetFilters">
              Reset Filter
            </button>
          </div>

          <div v-if="rows.length" class="history-footer">
            <small>
              Menampilkan {{ pagination.from }}–{{ pagination.to }} dari
              {{ pagination.total }} employee
            </small>
            <paginate
              v-if="pagination.lastPage > 1"
              :page-count="pagination.lastPage"
              :click-handler="fetchHistory"
              :force-page="pagination.currentPage - 1"
              :container-class="'pagination mb-0'"
              :page-class="'page-item'"
              :prev-text="'‹'"
              :next-text="'›'"
            />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "EmployeePasswordHistory",

  data() {
    return {
      loading: false,
      errorMessage: "",
      search: "",
      source: "",
      rows: [],
      requestSequence: 0,
      pagination: {
        currentPage: 1,
        lastPage: 1,
        perPage: 0,
        total: 0,
        from: 0,
        to: 0,
      },
    };
  },

  created() {
    this.fetchHistory(1);
  },

  computed: {
    hasActiveFilters() {
      return Boolean(this.search || this.source);
    },
  },

  methods: {
    async fetchHistory(pageNum) {
      const page = Number(pageNum) || 1;
      const requestId = ++this.requestSequence;

      this.loading = true;
      this.errorMessage = "";

      try {
        const response = await axios.post(
          `employee/password-history?page=${page}`,
          {
            search: {
              keyword: this.search || "",
            },
            source: this.source || "",
          },
        );

        if (requestId !== this.requestSequence) return;

        this.applyResponse(response.data, page);
      } catch (error) {
        if (requestId !== this.requestSequence) return;

        this.rows = [];
        this.resetPagination();
        this.errorMessage = this.resolveErrorMessage(error);
      } finally {
        if (requestId === this.requestSequence) {
          this.loading = false;
        }
      }
    },

    applyResponse(payload, requestedPage) {
      const source = this.responseSource(payload);
      const rows = Array.isArray(source)
        ? source
        : source && Array.isArray(source.data)
        ? source.data
        : [];

      const currentPage = Number(source.current_page || requestedPage || 1);
      const perPage = Number(source.per_page || rows.length || 0);
      const total = Number(source.total !== undefined ? source.total : rows.length);
      const from = Number(
        source.from !== undefined
          ? source.from || 0
          : rows.length
          ? (currentPage - 1) * perPage + 1
          : 0
      );

      this.rows = rows;
      this.pagination = {
        currentPage: currentPage,
        lastPage: Math.max(Number(source.last_page || 1), 1),
        perPage: perPage,
        total: total,
        from: from,
        to: Number(source.to !== undefined ? source.to || 0 : from + rows.length - 1),
      };
    },

    responseSource(payload) {
      let source = payload || {};

      if (
        source.result !== undefined &&
        source.result !== null &&
        typeof source.result === "object"
      ) {
        source = source.result;
      } else if (source.data !== undefined) {
        if (Array.isArray(source.data) && source.meta) {
          return Object.assign({}, source.meta, { data: source.data });
        }

        source = source.data;
      }

      if (
        source &&
        !Array.isArray(source) &&
        source.data &&
        !Array.isArray(source.data) &&
        Array.isArray(source.data.data)
      ) {
        source = source.data;
      }

      return source || {};
    },

    resetPagination() {
      this.pagination = {
        currentPage: 1,
        lastPage: 1,
        perPage: 0,
        total: 0,
        from: 0,
        to: 0,
      };
    },

    searchHistory() {
      this.fetchHistory(1);
    },

    applyFilters() {
      this.fetchHistory(1);
    },

    refreshHistory() {
      this.fetchHistory(this.pagination.currentPage);
    },

    resetFilters() {
      this.search = "";
      this.source = "";
      this.fetchHistory(1);
    },

    employee(row) {
      return row && row.employee && typeof row.employee === "object"
        ? row.employee
        : row || {};
    },

    field(row, key) {
      const employee = this.employee(row);
      return employee[key] !== undefined && employee[key] !== null
        ? employee[key]
        : row && row[key] !== undefined && row[key] !== null
        ? row[key]
        : "";
    },

    employeeName(row) {
      const fullName =
        this.field(row, "employee_name") ||
        this.field(row, "name") ||
        this.field(row, "full_name");
      if (fullName) return fullName;

      const name = [this.field(row, "firstname"), this.field(row, "lastname")]
        .filter(Boolean)
        .join(" ");

      return name || "Unknown Employee";
    },

    employeeInitial(row) {
      const name = this.employeeName(row);
      const words = name.split(" ").filter(Boolean);
      return words
        .slice(0, 2)
        .map((word) => word.charAt(0))
        .join("")
        .toUpperCase();
    },

    employeePosition(row) {
      const employee = this.employee(row);
      return (
        this.field(row, "job_position_name") ||
        this.field(row, "position_name") ||
        (employee.job_position && employee.job_position.title) ||
        "Employee"
      );
    },

    sourceLabel(source) {
      if (!source) return "Unknown";

      return String(source)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    },

    changedAt(row) {
      const value =
        row.password_changed_at ||
        row.last_password_changed_at ||
        row.password_updated_at ||
        row.changed_at ||
        row.updated_at ||
        this.field(row, "password_changed_at") ||
        this.field(row, "last_password_changed_at") ||
        this.field(row, "password_updated_at") ||
        this.field(row, "updated_at");

      if (!value) return "-";

      return this.$moment(value).format("DD/MM/YYYY - HH:mm:ss");
    },

    rowKey(row, index) {
      return row.id || row.history_id || `${this.field(row, "nik")}-${index}`;
    },

    rowNumber(index) {
      return (this.pagination.currentPage - 1) * this.pagination.perPage + index + 1;
    },

    resolveErrorMessage(error) {
      if (error.response && error.response.data && error.response.data.msg) {
        return error.response.data.msg;
      }

      if (error.response && error.response.status === 403) {
        return "Anda tidak memiliki akses untuk melihat password history.";
      }

      return "Periksa koneksi atau coba muat ulang halaman.";
    },
  },
};
</script>

<style lang="scss" scoped>
.password-history-page {
  padding-bottom: 30px;
}

.history-hero {
  margin-bottom: 20px;
  color: #fff;
  border: 0;
  overflow: hidden;
  background: linear-gradient(120deg, #103c82 0%, #0aaebf 100%);

  .card-body {
    padding: 24px 28px;
  }

  h4 {
    margin: 4px 0 6px;
    color: #fff;
    font-weight: 700;
  }

  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.78);
  }
}

.hero-content,
.history-toolbar,
.history-footer,
.history-message {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: #a7f3d0;
}

.history-total {
  display: flex;
  align-items: center;
  min-width: 160px;
  padding: 12px 18px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.12);

  > i {
    margin-right: 13px;
    font-size: 26px;
  }

  strong,
  span {
    display: block;
  }

  strong {
    font-size: 22px;
    line-height: 1.1;
  }

  span {
    margin-top: 2px;
    font-size: 11px;
    color: rgba(255, 255, 255, 0.75);
  }
}

.history-toolbar {
  gap: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #edf0f4;
}

.history-filters {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  padding: 16px 0;
  border-bottom: 1px solid #edf0f4;
  background: #fbfcfe;

  .filter-group {
    width: 230px;
  }

  label {
    display: block;
    margin-bottom: 6px;
    color: #687386;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
  }
}

.reset-filter {
  height: 38px;
}

.history-search {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 500px;

  .input-group {
    flex: 1;
  }

  .form-control {
    height: 40px;
  }
}

.refresh-button {
  width: 42px;
  height: 40px;
  margin-left: 8px;
}

.history-loading,
.history-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 340px;
  color: #7a8492;
}

.history-loading .spinner-border {
  margin-bottom: 14px;
}

.history-message {
  gap: 20px;
  margin: 20px 0 0;

  span {
    display: block;
    margin-top: 3px;
  }
}

.history-table-wrapper {
  overflow-x: auto;
}

.history-table {
  min-width: 1050px;
  margin: 0;

  thead th {
    padding: 15px 13px;
    border-top: 0;
    border-bottom: 1px solid #dee4ea;
    color: #77808d;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.35px;
    text-transform: uppercase;
    white-space: nowrap;
  }

  tbody td {
    padding: 15px 13px;
    border-color: #edf0f4;
    vertical-align: middle;
    color: #364152;
  }

  tbody tr:hover {
    background: #f8fbff;
  }
}

.number-column {
  width: 58px;
  color: #98a1ad !important;
  text-align: center;
}

.employee-cell {
  display: flex;
  align-items: center;
  min-width: 220px;

  strong,
  small {
    display: block;
  }

  small {
    margin-top: 3px;
    color: #9099a5;
  }
}

.employee-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  margin-right: 12px;
  border-radius: 12px;
  color: #1769aa;
  background: #e8f4ff;
  font-size: 12px;
  font-weight: 700;
}

.nik-value {
  padding: 5px 9px;
  border-radius: 6px;
  color: #495568;
  background: #f1f4f7;
  font-family: monospace;
  font-size: 12px;
  white-space: nowrap;
}

.contact-cell {
  min-width: 190px;

  span,
  small {
    display: block;
  }

  small {
    margin-top: 3px;
    color: #9099a5;
  }
}

.date-cell {
  display: flex;
  align-items: center;
  min-width: 175px;
  white-space: nowrap;

  i {
    margin-right: 8px;
    color: #0aaebf;
  }
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 20px;
  color: #197241;
  background: #e8f8ef;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;

  i {
    margin-right: 5px;
  }
}

.source-badge {
  display: inline-block;
  padding: 5px 9px;
  border-radius: 6px;
  color: #5a4a12;
  background: #fff6d8;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.history-empty {
  text-align: center;

  .empty-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 68px;
    height: 68px;
    margin-bottom: 18px;
    border-radius: 20px;
    color: #0aaebf;
    background: #eaf9fb;
    font-size: 28px;
  }

  h5 {
    color: #364152;
  }

  p {
    max-width: 420px;
  }
}

.history-footer {
  gap: 20px;
  padding-top: 18px;
  border-top: 1px solid #edf0f4;
  color: #7a8492;
}

.rotating {
  animation: rotate 0.8s linear infinite;
}

@keyframes rotate {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 767px) {
  .hero-content,
  .history-toolbar,
  .history-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .history-total {
    margin-top: 18px;
  }

  .history-search {
    width: 100%;
  }

  .history-filters {
    align-items: stretch;
    flex-direction: column;

    .filter-group {
      width: 100%;
    }
  }

  .history-footer {
    align-items: center;
  }
}
</style>
